import cn from 'classnames';
import { User } from 'common/interfaces/api';
import { linkRegexFactory, truncateStr } from 'common/utils';
import { isIzanaviMember, isMembershipAccount } from 'common/utils/membership';
import { ScreenName } from 'common/utils/pp_tracking';
import ImagePreviewDialog from 'components/common/ImagePreviewDialog';
import MembershipVipIcon from 'components/common/MembershipVipIcon';
import { LocationIcon, VerifyCreatorIcon } from 'components/v2/atoms/icon';
import FollowButton from 'components/v2/modules/profile-page/follow-button';
import MoreOptions from 'components/v2/modules/profile-page/more-options';
import PrimeSubButton from 'components/v2/modules/profile-page/prime-sub-button';
import ProfileAvatar from 'components/v2/modules/profile-page/profile-avatar';
import ProfileCoverImage from 'components/v2/modules/profile-page/profile-cover-image';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

const ProfileJoinDate = dynamic(() => import('./profile-join-date'), {
  ssr: false,
});

// Interface for profile info props
interface ProfileInfoPropsType {
  user: User;
  className?: string;
  isSettingsPage?: boolean;
}

// Format number with locale (e.g., 12345 -> "12,345" or "1.2万")
const formatFollowCount = (count: number): string => {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  return count.toLocaleString('ja-JP');
};

const ProfileInfo = ({
  user,
  className,
  isSettingsPage = false,
}: ProfileInfoPropsType) => {
  const currentUser = useStore((state) => state.currentUser);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const setShowMembershipPayDialog = useGlobalStore(
    (state) => state.setShowMembershipPayDialog,
  );
  const isCurrentUser = user?.username === currentUser?.username;

  // Image preview state
  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const [isPreviewAvatar, setIsPreviewAvatar] = useState(false);

  const handleImagePreview = useCallback(
    (imageUrl: string, isAvatar = false) => {
      setPreviewImageUrl(imageUrl);
      setIsPreviewAvatar(isAvatar);
      setOpenImagePreview(true);
    },
    [],
  );

  const handleMembershipClick = useCallback(() => {
    if (!currentUser) {
      showLogin({});
      return;
    }
    setShowMembershipPayDialog({
      open: true,
      showMembershipStatus: false,
      screen: ScreenName.Profile,
    });
  }, [currentUser, showLogin, setShowMembershipPayDialog]);

  // Parse followers count
  const getFollowersCount = (): number => {
    if (!user?.follow_count) return 0;
    const count = parseInt(user.follow_count.replace(/,/g, ''), 10);
    return isNaN(count) ? 0 : count;
  };

  // Get following count
  const getFollowingCount = (): number => {
    return Number(user?.following_count) || 0;
  };

  if (!user) return null;

  return (
    <div
      className={cn(
        'all-font-sans w-full overflow-hidden rounded-b-lg border border-t-0 border-pp-border-primary bg-pp-bg-primary',
        className ?? '',
      )}
    >
      {/* Cover Image Section */}
      <ProfileCoverImage
        user={user}
        isCurrentUser={isCurrentUser}
        isSettingsPage={isSettingsPage}
        onImagePreview={(imageUrl) => handleImagePreview(imageUrl, false)}
      />

      {/* Profile Content */}
      <div className="relative px-4 pb-4">
        {/* Avatar Section - overlapping cover image */}
        <div className="flex items-start justify-between">
          <ProfileAvatar
            user={user}
            isCurrentUser={isCurrentUser}
            isSettingsPage={isSettingsPage}
            onImagePreview={handleImagePreview}
          />

          {/* Action Buttons */}
          <div className="mt-3 flex items-center gap-2">
            {!isCurrentUser && !isSettingsPage && (
              <>
                {/* Prime Registration Button */}
                <PrimeSubButton user={user} />

                {/* Follow Button with Dropdown */}
                <FollowButton user={user} />

                {/* More Options Dropdown */}
                <MoreOptions user={user} />
              </>
            )}
            {isCurrentUser && !isSettingsPage && (
              <Link
                href="/account?tab=info"
                className="flex items-center gap-1 rounded-full border border-pp-border-primary px-4 py-1.5 text-sm font-medium text-pp-text-primary transition-colors hover:bg-pp-bg-secondary"
              >
                プロフィールを設定
              </Link>
            )}
          </div>
        </div>

        {/* User Name Section */}
        <div className="mt-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold leading-none text-pp-text-primary">
              {user.name}
            </h1>

            {/* Verified/Advertiser Badge */}
            {user.is_creator && (
              <VerifyCreatorIcon className="size-5 text-pp-green-primary" />
            )}

            {/* Membership Badge */}
            {(isMembershipAccount(user) || isIzanaviMember(user)) && (
              <span className="cursor-pointer" onClick={handleMembershipClick}>
                <MembershipVipIcon
                  packageName={
                    user.membership?.membership_package?.package_name
                  }
                  width={50}
                  height={26}
                  isIzanavi={isIzanaviMember(user)}
                />
              </span>
            )}
          </div>

          {/* Username */}
          {/* <p className="text-base text-pp-text-secondary">@{user.username}</p> */}
        </div>

        {/* Job Section */}
        {user.job && (
          <p className="mt-1 text-sm text-pp-text-secondary">{user.job}</p>
        )}

        {/* Bio Section */}
        {user.bio && (
          <div className="scrollbar-default mt-1 max-h-[250px] overflow-y-auto">
            <p
              className={cn(
                'whitespace-pre-wrap text-sm leading-relaxed text-pp-text-primary',
              )}
            >
              {user.bio}
            </p>
          </div>
        )}

        {/* Meta Info (Location, Website, Join Date) */}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-pp-text-secondary">
          {/* Location */}
          {user.country?.name && (
            <div className="flex items-center gap-1">
              <LocationIcon className="size-4" />
              <span>{user.country.name}</span>
            </div>
          )}

          {/* Join Date */}
          {user?.created_at && <ProfileJoinDate createdAt={user.created_at} />}

          {/* Recommend URL */}
          {user?.recommend_url &&
            linkRegexFactory().test(user.recommend_url) && (
              <div className="flex items-center gap-1">
                <svg
                  className="size-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
                <a
                  href={user.recommend_url ?? ''}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="truncate text-pp-green-primary hover:underline"
                >
                  {truncateStr(
                    decodeURI(user.recommend_url?.replace(/^https?:\/\//, '')),
                    50,
                  )}
                </a>
              </div>
            )}
        </div>
        {/* Follow Stats */}
        <div className="mt-2 flex items-center gap-4">
          {/* Following */}
          {isCurrentUser ? (
            <Link
              href={`/account/following_users?from=${encodeURIComponent(user.username)}`}
              className="flex items-center hover:opacity-70"
            >
              <span className="font-bold text-pp-text-primary">
                {formatFollowCount(getFollowingCount())}
              </span>
              <span className="ml-1 text-pp-text-secondary">フォロー中</span>
            </Link>
          ) : (
            // <div className="flex items-center">
            //   <span className="font-bold text-pp-text-primary">
            //     {formatFollowCount(getFollowingCount())}
            //   </span>
            //   <span className="ml-1 text-pp-text-secondary">フォロー中</span>
            // </div>
            <></>
          )}
          {/* Followers */}
          {isCurrentUser ? (
            <Link
              href={`/account/follower_users?from=${encodeURIComponent(user.username)}`}
              className="flex items-center hover:opacity-70"
            >
              <span className="font-bold text-pp-text-primary">
                {formatFollowCount(getFollowersCount())}
              </span>
              <span className="ml-1 text-pp-text-secondary">フォロワー</span>
            </Link>
          ) : (
            <div className="flex items-center">
              <span className="font-bold text-pp-text-primary">
                {formatFollowCount(getFollowersCount())}
              </span>
              <span className="ml-1 text-pp-text-secondary">フォロワー</span>
            </div>
          )}
          {/* Badge Level */}
          {user.badge && (
            <a
              href="https://help.postprime.com/hc/ja/articles/4414706319759"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-pp-text-secondary hover:opacity-70"
            >
              <svg
                className="size-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {/* Badge circle */}
                <circle cx="12" cy="10" r="6" strokeWidth={1.5} />
                {/* Ribbon left */}
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 15l-2 6 3-2 1.5 2.5L12 16"
                />
                {/* Ribbon right */}
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M16 15l2 6-3-2-1.5 2.5L12 16"
                />
              </svg>
              <span className="font-bold text-pp-text-primary">
                バッジレベル{user.badge.replace('level', '')}
              </span>
            </a>
          )}
        </div>
      </div>

      {/* Image Preview Dialog */}
      {openImagePreview && (
        <ImagePreviewDialog
          imgSrc={previewImageUrl}
          open={openImagePreview}
          setOpen={setOpenImagePreview}
          isAvatar={isPreviewAvatar}
        />
      )}
    </div>
  );
};

export default ProfileInfo;
